import { Injectable } from '@angular/core';
import { environment } from "src/environments/environment";

declare var Keycloak: any;

@Injectable({
  providedIn: 'root'
})
export class KeycloakService {

  public keycloakAuth: any;
  private readonly loginGuardKey = 'kc-login-started';
  private readonly postLoginHashKey = 'kc-post-login-hash';
  // HashLocationStrategy: Keycloak Valid Redirect URIs must stay on origin+/
  // (no hash). Deep links live in the fragment — save/restore them around login.
  private readonly redirectUri = window.location.origin + '/';
  private handlingUnauthorized = false;
  private refreshInFlight: Promise<string> | null = null;
  private forceRefreshInFlight: Promise<string> | null = null;
  private expiryRefresh = false;
  private savedIdToken: string | null = null;
  private savedIdTokenParsed: any = null;

  constructor() { }

  async init(): Promise<any> {
    const config = {
      url: environment.AUTH_RACINE_URL,
      realm: environment.REALM,
      clientId: environment.CLIENT_ID
    };

    this.keycloakAuth = new Keycloak(config);
    // Must be set before init: setToken() arms the expiry timer only if this exists.
    // Refresh in place. A reload would drop the new tokens (they live in memory only).
    this.keycloakAuth.onTokenExpired = () => {
      if (this.expiryRefresh) {
        return;
      }
      this.expiryRefresh = true;
      this.ensureToken(-1)
        .catch((error) => {
          console.warn("Keycloak access token refresh failed", error);
          this.login();
        })
        .then(() => {
          this.expiryRefresh = false;
        });
    };

    // Persist Angular route (#/...) before Keycloak login/callback wipes the URL
    this.savePostLoginHash();

    // Detect OAuth callback BEFORE keycloak clears the URL via replaceState
    const hadCallback = /[?#](.*&)?(code|error)=/.test(window.location.href)
      || new URLSearchParams(window.location.search).has('code')
      || new URLSearchParams(window.location.search).has('error');

    try {
      // useNonce: false — keycloak-js 22 rejects tokens from Keycloak 26 because
      // access tokens no longer carry a nonce claim (only id_token does), which
      // triggers "Invalid nonce, clearing token" after a successful /token exchange.
      // responseMode query — required with Angular HashLocationStrategy.
      // No onLoad login-required — avoids infinite SSO redirect on callback failure.
      const authenticated = await this.keycloakAuth.init({
        checkLoginIframe: false,
        responseMode: 'query',
        pkceMethod: 'S256',
        redirectUri: this.redirectUri,
        useNonce: false,
      });

      if (authenticated) {
        sessionStorage.removeItem(this.loginGuardKey);
        this.restorePostLoginHash();
        return;
      }

      // Stale/failed OAuth callback (?code= without usable PKCE state, wrong auth
      // host, reused code, etc.) → blank page if we throw. Strip params and
      // restart login once; loginGuard still blocks an endless loop.
      if (hadCallback) {
        console.warn(
          'Keycloak callback received but session was not established. ' +
          'Clearing OAuth params and restarting login. Check Network → POST .../openid-connect/token.',
          { url: config.url, realm: config.realm, clientId: config.clientId }
        );
        this.stripOAuthParamsFromUrl();
        // Drop leftover PKCE/callback entries from a previous attempt.
        Object.keys(localStorage)
          .filter((k) => k.indexOf('kc-callback-') === 0)
          .forEach((k) => localStorage.removeItem(k));
      }

      if (sessionStorage.getItem(this.loginGuardKey) === '1') {
        sessionStorage.removeItem(this.loginGuardKey);
        throw new Error(
          'Keycloak redirect loop prevented. Clear site data for localhost and ' +
          config.url + ' then retry.'
        );
      }

      sessionStorage.setItem(this.loginGuardKey, '1');
      await this.keycloakAuth.login({ redirectUri: this.redirectUri });
    } catch (error) {
      const err = error || new Error('Keycloak init failed (empty error — often invalid nonce or /token rejection)');
      console.error('Keycloak init failed', {
        url: config.url,
        realm: config.realm,
        clientId: config.clientId,
        hadCallback,
        error: err,
      });
      throw err;
    }
  }

  /** Keep #/deep-link across Keycloak redirect to origin+/ */
  private savePostLoginHash(): void {
    const hash = window.location.hash;
    if (hash && hash !== '#' && hash !== '#/') {
      sessionStorage.setItem(this.postLoginHashKey, hash);
    }
  }

  private restorePostLoginHash(): void {
    const hash = sessionStorage.getItem(this.postLoginHashKey);
    if (!hash) {
      return;
    }
    sessionStorage.removeItem(this.postLoginHashKey);
    if (window.location.hash !== hash) {
      window.location.hash = hash;
    }
  }

  /** Remove ?code=&state= so a failed callback cannot poison the next init. */
  private stripOAuthParamsFromUrl(): void {
    const url = new URL(window.location.href);
    ['code', 'state', 'session_state', 'iss', 'error', 'error_description'].forEach((p) =>
      url.searchParams.delete(p)
    );
    const cleaned = url.origin + url.pathname + (url.search || '') + (url.hash || '');
    window.history.replaceState({}, '', cleaned);
  }

  getToken(): string {
    return this.keycloakAuth && this.keycloakAuth.token;
  }

  /**
   * Returns a usable access token.
   * minValidity 30 refreshes only when the access token expires within 30s.
   * minValidity -1 always exchanges the refresh token (API 401).
   * Parallel callers share a single /token request. Do not reload afterwards.
   */
  ensureToken(minValidity = 30): Promise<string> {
    if (!this.keycloakAuth) {
      return Promise.resolve('');
    }
    if (!this.keycloakAuth.refreshToken) {
      if (minValidity === -1) {
        return Promise.reject(new Error('no refresh token'));
      }
      return Promise.resolve(this.getToken() || '');
    }

    const forced = minValidity === -1;
    const current = forced ? this.forceRefreshInFlight : this.refreshInFlight;
    if (current) {
      return current;
    }

    this.rememberIdToken();
    return this.shareRefresh(minValidity, forced);
  }

  private shareRefresh(minValidity: number, forced: boolean): Promise<string> {
    const state: { run: Promise<string> | null } = { run: null };
    const run = this.startRefresh(minValidity).finally(() => {
      if (forced) {
        if (this.forceRefreshInFlight === state.run) {
          this.forceRefreshInFlight = null;
        }
      } else if (this.refreshInFlight === state.run) {
        this.refreshInFlight = null;
      }
    });
    state.run = run;
    if (forced) {
      this.forceRefreshInFlight = run;
    } else {
      this.refreshInFlight = run;
    }
    return run;
  }

  private startRefresh(minValidity: number): Promise<string> {
    try {
      return this.keycloakAuth.updateToken(minValidity).then(() => {
        this.restoreIdTokenIfMissing();
        return this.getToken() || '';
      });
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Full login when the refresh token is missing or rejected.
   * A fresh access token that the API still rejects must not come here:
   * that would loop on SSO.
   */
  login(): Promise<void> {
    if (this.handlingUnauthorized || !this.keycloakAuth) {
      return Promise.resolve();
    }
    this.handlingUnauthorized = true;
    this.savePostLoginHash();
    sessionStorage.removeItem(this.loginGuardKey);
    return this.keycloakAuth.login({ redirectUri: this.redirectUri });
  }

  /** Keycloak often omits id_token on refresh and keycloak-js then deletes it. */
  private rememberIdToken(): void {
    if (this.keycloakAuth && this.keycloakAuth.idToken) {
      this.savedIdToken = this.keycloakAuth.idToken;
      this.savedIdTokenParsed = this.keycloakAuth.idTokenParsed;
    }
  }

  private restoreIdTokenIfMissing(): void {
    if (this.keycloakAuth && !this.keycloakAuth.idToken && this.savedIdToken) {
      this.keycloakAuth.idToken = this.savedIdToken;
      this.keycloakAuth.idTokenParsed = this.savedIdTokenParsed;
    }
  }

  logout(): any {
    sessionStorage.removeItem(this.loginGuardKey);
    sessionStorage.removeItem(this.postLoginHashKey);
    return this.keycloakAuth.logout({ redirectUri: this.redirectUri });
  }

  getName(): string {
    return this.keycloakAuth.idTokenParsed.name;
  }

  getUsername(): string {
    return this.keycloakAuth.idTokenParsed.preferred_username;
  }

  getAccessPermissions(): string[] {
    return this.keycloakAuth.realmAccess.roles;
  }
}
